import { DataHistory, DataModel } from '@shared/models/data-model';
import { NavigationItemType } from '@shared/enum/navigation-item-type';
import { EgiPage, PageFeature } from '@ervo/page-framework';
import { by } from '@common/utils/array-utils';
import { cloneDeep } from 'lodash-es';

/**
 * Holds information about the configured routes, features, page, ... in the application.
 * A navigationItem always exists inside a EgiApp.
 */

const NO_FEATURES: PageFeature[] = [];

// Cache voor asArray in een WeakMap en niet als property op de instantie, zodat hij ook op
// bevroren objecten werkt (Akita bevroor store-state in dev-mode; sinds de Angular 21-upgrade
// is Akita weg, het patroon blijft omdat het de modellen vrij houdt van mutatie).
const asArrayCache = new WeakMap<NavigationItemData, NavigationItemData[]>();

export class NavigationItemData extends DataModel {
  constructor(
    id: string,
    metaData: Partial<DataHistory>,
    public appId: string | undefined,
    public parentId: string | undefined,
    public order: number,
    // @ts-ignore this just works but for some reason it gives a syntax error
    public children: this[],
    public pageComponentId: string | undefined,
    public type: NavigationItemType,
    public egiPage: EgiPage | undefined,
    public name: string,
    public config: any | undefined
  ) {
    super(id, metaData);
  }

  get hasChildren(): boolean {
    return !!this.children.length;
  }

  get isMenu(): boolean {
    return this.type === NavigationItemType.Menu;
  }

  get isRoot(): boolean {
    return this.type === NavigationItemType.AppRoot;
  }

  get descendants(): this[] {
    const asArray = cloneDeep(this.asArray);
    asArray.unshift();
    return asArray;
  }

  get regularFeatures(): PageFeature[] {
    return this.egiPage?.regularFeatures ?? NO_FEATURES;
  }

  get hasDescendantThatShouldBeSynced(): boolean {
    return this.children.some(c => c.shouldBeSynced || c.hasDescendantThatShouldBeSynced);
  }

  get shouldBeSynced(): boolean {
    return (
      this.lacksEgiPage || this.hasMissingClickTroughPages || this.hasObsoleteClickThroughPages
    );
  }

  get hasEgiPage(): boolean {
    return !!this.egiPage;
  }

  get lacksEgiPage(): boolean {
    if (this.type === NavigationItemType.Menu) {
      return false;
    }
    return !this.hasEgiPage;
  }

  get isExternalPage(): boolean {
    return this.egiPage.path == 'external-url';
  }

  get hasMissingClickTroughPages(): boolean {
    if (this.type === NavigationItemType.Menu) {
      return false;
    }
    return this.missingClickThroughPageIds.length > 0;
  }

  get missingClickThroughPageIds(): string[] {
    const clickTroughConfigs = this.egiPage?.config?.clickTroughPages;
    if (!clickTroughConfigs) {
      return [];
    }

    const clickTroughIds = Object.keys(clickTroughConfigs).map(k => clickTroughConfigs[k].id);
    const existingClickTroughIds = this.children.map(c => c.pageComponentId);
    return clickTroughIds.filter(id => !existingClickTroughIds.includes(id));
  }

  get hasObsoleteClickThroughPages(): boolean {
    if (this.type === NavigationItemType.Menu) {
      return false;
    }
    return !!this.obsoleteClickTroughPageIds.length;
  }

  get obsoleteClickTroughPageIds(): string[] {
    const savedClickTroughIds = this.clickThroughChildren.map(c => c.pageComponentId);
    return savedClickTroughIds.filter(id => !this.clickTroughPageIds.includes(id));
  }

  get clickTroughPageIds(): string[] {
    const clickTroughConfigs = this.egiPage?.config.clickTroughPages;
    if (!clickTroughConfigs) {
      return [];
    }

    return Object.keys(clickTroughConfigs).map(k => clickTroughConfigs[k].id);
  }

  get clickThroughChildren(): this[] {
    return this.children.filter(c => c.isClickTroughPage);
  }

  get isClickTroughPage(): boolean {
    return this.type === NavigationItemType.ClickThrough;
  }

  get hasWiki(): boolean {
    return !!this.config?.wiki;
  }

  get wikiUrl(): string | undefined {
    return this.config?.wiki;
  }

  sortDescendants(): void {
    this.children.sort(by('order'));
    this.children.forEach(c => c.sortDescendants());
  }

  findDescendant(id: string): this {
    if (this.id === id) {
      return this;
    }
    const descendant = this.findDescendantRecursive(id);
    if (!descendant) {
      throw new Error('Descendant not found in tree!');
    }

    return descendant;
  }

  forEachDescendant(cb: (i: this) => void) {
    const children = cloneDeep(this.asArray);
    children.unshift();

    children.forEach(cb);
  }

  private findDescendantRecursive(id: string): this | false {
    for (const child of this.children) {
      if (child.id === id) {
        return child;
      }

      const descendant = child.findDescendantRecursive(id);
      if (descendant) {
        return descendant;
      }
    }

    return false;
  }

  get asArray(): this[] {
    let asArray = asArrayCache.get(this) as this[] | undefined;
    if (!asArray) {
      asArray = [];
      this.fillAsArray(this, asArray);
      asArrayCache.set(this, asArray);
    }

    return asArray;
  }

  private fillAsArray(item: this, into: this[]) {
    into.push(item);
    item.children.forEach(c => this.fillAsArray(c as this, into));
  }

  findAncestors(rootItem: this): this[] {
    if (!this.isRelatedTo(rootItem)) {
      throw new Error('The passed item is not related to this navigationItem');
    }

    const wholeFamily = rootItem.asArray;
    const foundAncestors: this[] = [];
    // eslint-disable-next-line @typescript-eslint/no-this-alias
    let currentItem = this;
    while (currentItem && !currentItem.isRoot) {
      currentItem = wholeFamily.find(i => i.id === currentItem.parentId);
      foundAncestors.push(currentItem);

      if (currentItem === rootItem) {
        break;
      }
    }

    return foundAncestors;
  }

  isRelatedTo(target: this): boolean {
    return target.appId === this.appId;
  }
}
